import React, { useState } from 'react';
import { Clock } from 'lucide-react';
import Chip from '@mui/material/Chip';
import { useManufacturerHistory } from '../../../services/api/domains/manufacturers/hooks';
import { ManufacturerHistoryEvent } from '../../../services/api/domains/manufacturers/types';
import { formatDateOnly, formatTimestamp } from '../../../utils/dateConversion';
import {
  GRAY_200,
  GRAY_400,
  GRAY_500,
  GRAY_700,
  GRAY_900,
} from 'theme/grayScale';
import { COLOR_DANGER, COLOR_INFO } from 'theme/colors';

// Events whose source column is a SQL DATE (no time component): GLEIF
// incorporation, USDMF submit date, Orange Book approval date, NDC marketing
// start date. Shown as the raw date -- never converted to the viewer's zone.
// Every other event type is a recorded UTC timestamp, shown in the viewer's zone.
const DATE_ONLY_EVENT_TYPES: ReadonlySet<ManufacturerHistoryEvent['type']> =
  new Set<ManufacturerHistoryEvent['type']>([
    'incorporation',
    'usdmf_filing',
    'orange_book_approval',
    'ndc_marketing',
  ]);

const EVENT_TYPE_LABELS: Record<ManufacturerHistoryEvent['type'], string> = {
  merge: 'Duplicate Merged',
  name_capture: 'Name Capture',
  incorporation: 'Incorporation',
  rebrand: 'Rebrand',
  preferred_name_change: 'Preferred Name Changed',
  parent_link: 'Parent/Subsidiary',
  usdmf_filing: 'USDMF Filing',
  orange_book_approval: 'FDA Approval',
  ndc_marketing: 'NDC Marketing',
  previous_legal_name: 'Previous Legal Name',
  successor_candidate: 'Successor Match',
  decrs_divergence: 'DECRS Divergence (Inferred)',
  usdmf_relink: 'USDMF Holder Relinked',
};

// Fixed display order for the chip filter -- every possible event type,
// always shown (even at a 0 count), so a reviewer can see the full set of
// options and which ones are currently empty for this manufacturer, not
// just the types that happen to already be present.
const ALL_EVENT_TYPES = Object.keys(EVENT_TYPE_LABELS) as Array<
  ManufacturerHistoryEvent['type']
>;

interface HistoryTabProps {
  manufacturerId: number;
}

/**
 * Manufacturer modal History tab (issue #653, Task 10, Sub-task 10.6) —
 * timeline of recorded name and ownership changes. Sources: merge trail
 * (`Manufacturers_Aliases` — an internal duplicate-record consolidation,
 * NOT a real-world corporate merger), synonym first/last-seen and rebrand
 * detection (`Manufacturer_Names`), GLEIF incorporation date,
 * parent/subsidiary links (`Audit_Log`, written by e.g. `scripts/seed-
 * manufacturer-hierarchy.js`), dated filing trails from USDMF/Orange
 * Book/NDC (MFGV2-009, Sub-task 9.1), GLEIF previous legal names
 * (MFGV2-010, Sub-task 10.2 — genuinely undated, GLEIF gives no date for
 * these; rendered with an explicit "Undated" label, never a blank or
 * garbage date), GLEIF successor-merge candidates (MFGV2-011, Sub-task
 * 11.1 — a review-queue row, deliberately distinct from `parent_link`
 * since Requirement 19 treats a successor as a merge candidate, never an
 * auto-applied hierarchy link), and a DECRS registrant/firm name
 * divergence (MFGV2-012, Sub-task 12.1 — a point-in-time inference, also
 * undated; its type badge says "(Inferred)" explicitly, distinguishing it
 * from `previous_legal_name`'s directly-reported-but-undated GLEIF fact).
 * Does NOT include an iRES successor-FEI source (`firmsurvivingfei`) —
 * that data isn't imported yet (blocked on the same Explorer blob-auth
 * Human Task as Sub-task 6.6/6.4).
 *
 * ~1% of records are expected to have any history at all (AC) — the empty
 * state below is deliberately the COMMON case, styled to read as normal
 * rather than as a missing-data error.
 */
const HistoryTab: React.FC<HistoryTabProps> = ({ manufacturerId }) => {
  const historyQuery = useManufacturerHistory(manufacturerId);
  // Chip-based multi-select event-type filter (MFGV3-007, Sub-task 7.1).
  // OR semantics -- selecting more chips shows MORE events, not fewer.
  // Empty selection (initial state, or after deselecting all) shows the
  // full unfiltered list -- "no filter applied" is never treated as
  // "filter matched nothing."
  const [selectedTypes, setSelectedTypes] = useState<
    Set<ManufacturerHistoryEvent['type']>
  >(new Set());

  const toggleType = (type: ManufacturerHistoryEvent['type']) => {
    setSelectedTypes(prev => {
      const next = new Set(prev);
      if (next.has(type)) {
        next.delete(type);
      } else {
        next.add(type);
      }
      return next;
    });
  };

  if (historyQuery.status === 'error') {
    return (
      <div style={{ color: COLOR_DANGER, fontSize: '13px', padding: '8px 0' }}>
        Failed to load history. Try closing and reopening the modal.
      </div>
    );
  }

  if (historyQuery.status === 'idle' || historyQuery.status === 'loading') {
    return (
      <div style={{ color: GRAY_500, fontSize: '13px', padding: '8px 0' }}>
        Loading history...
      </div>
    );
  }

  const events = historyQuery.data;

  // Every event type always gets a chip, with its own count -- a reviewer
  // can see every filter option and, at a glance, which ones are 0 for
  // this manufacturer, rather than only the types already present.
  const eventCountByType = new Map<ManufacturerHistoryEvent['type'], number>();
  for (const type of ALL_EVENT_TYPES) {
    eventCountByType.set(type, 0);
  }
  for (const event of events) {
    eventCountByType.set(
      event.type,
      (eventCountByType.get(event.type) ?? 0) + 1
    );
  }
  const filteredEvents =
    selectedTypes.size === 0
      ? events
      : events.filter(event => selectedTypes.has(event.type));

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
      <div
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '6px',
          paddingBottom: '8px',
        }}>
        {ALL_EVENT_TYPES.map(type => (
          <Chip
            key={type}
            label={`${EVENT_TYPE_LABELS[type]} (${eventCountByType.get(type)})`}
            size="small"
            onClick={() => toggleType(type)}
            color={selectedTypes.has(type) ? 'primary' : 'default'}
            variant={selectedTypes.has(type) ? 'filled' : 'outlined'}
            aria-pressed={selectedTypes.has(type)}
            style={{ fontSize: '11px' }}
          />
        ))}
      </div>
      {events.length === 0 && (
        <div
          style={{
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: '8px',
            padding: '32px 16px',
            textAlign: 'center',
          }}>
          <Clock size={28} color={GRAY_400} />
          <div style={{ fontSize: '14px', fontWeight: 500, color: GRAY_700 }}>
            No recorded history
          </div>
          <div style={{ fontSize: '12px', color: GRAY_500, maxWidth: '320px' }}>
            This is normal — most manufacturers have no recorded name changes or
            mergers. History appears here once a name change, merger, or
            incorporation date is captured from a source.
          </div>
        </div>
      )}
      {events.length > 0 && filteredEvents.length === 0 && (
        <div
          style={{
            color: GRAY_500,
            fontSize: '13px',
            padding: '16px 0',
            textAlign: 'center',
          }}>
          No events match the selected filter(s).
        </div>
      )}
      {filteredEvents.map((event, idx) => (
        <div
          key={`${event.type}-${event.date}-${idx}`}
          style={{
            display: 'flex',
            gap: '12px',
            padding: '10px 8px',
            borderBottom:
              idx < filteredEvents.length - 1
                ? `1px solid ${GRAY_200}`
                : 'none',
          }}>
          <div
            style={{
              fontSize: '12px',
              color: GRAY_500,
              width: '150px',
              flexShrink: 0,
              fontStyle: event.date === null ? 'italic' : 'normal',
            }}>
            {event.date === null
              ? 'Undated'
              : DATE_ONLY_EVENT_TYPES.has(event.type)
                ? formatDateOnly(event.date)
                : formatTimestamp(event.date)}
          </div>
          <div style={{ flex: 1 }}>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '6px',
                marginBottom: '2px',
              }}>
              <span
                style={{
                  fontSize: '10px',
                  fontWeight: 700,
                  textTransform: 'uppercase',
                  letterSpacing: '0.04em',
                  color: COLOR_INFO,
                }}>
                {EVENT_TYPE_LABELS[event.type]}
              </span>
            </div>
            <div style={{ fontSize: '13px', color: GRAY_900 }}>
              {event.label}
            </div>
            {event.detail && (
              <div style={{ fontSize: '12px', color: GRAY_500 }}>
                {event.detail}
              </div>
            )}
          </div>
        </div>
      ))}
    </div>
  );
};

export default HistoryTab;
